/**
 * Auth token storage.
 *
 * Access + refresh tokens used to live in plaintext AsyncStorage
 * (`localStorage` on web, an unencrypted app-sandbox file on Android).
 * The refresh token in particular is a 7-day sliding credential — leaving
 * it in plaintext is close to permanent account access if the device is
 * compromised (rooted/jailbroken device, a backup extraction, a second
 * app with `MODE_WORLD_READABLE`-adjacent access, etc).
 *
 * On native (iOS/Android) this module routes through `expo-secure-store`,
 * which is backed by the iOS Keychain / Android Keystore — encrypted at
 * rest and outside the plain app-sandbox file tree. On web there's no
 * SecureStore (no Keychain/Keystore to back it), and web isn't the
 * app-store target for this hardening pass, so it keeps using AsyncStorage
 * as before. The same AsyncStorage fallback also keeps this module inert
 * in the Vitest/Node test harness, where the native module can't load —
 * existing tests already mock `@react-native-async-storage/async-storage`.
 *
 * SecureStore keys must match `[A-Za-z0-9.\-_]+` — 'accessToken' and
 * 'refreshToken' are fine. SecureStore also warns above ~2048 bytes per
 * value on iOS; the JWTs stored here (sub/email/tier/displayName/
 * avatarUrl/emailVerified claims) are well under that.
 */
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

type SecureStoreModule = {
  getItemAsync: (key: string) => Promise<string | null>;
  setItemAsync: (key: string, value: string) => Promise<void>;
  deleteItemAsync: (key: string) => Promise<void>;
};

// Lazy/defensive load — mirrors `loadPostHogCtor` in
// `./analytics/posthog-analytics.ts`. A literal require() so Metro
// bundles the native module for iOS/Android/web builds; in the
// Vitest/Node harness `expo-secure-store`'s entry point can't resolve
// its native binding and this throws, which we swallow so the module
// degrades to the AsyncStorage path below.
let SecureStore: SecureStoreModule | null = null;
try {
  SecureStore = require('expo-secure-store') as SecureStoreModule;
} catch {
  // Keep the `null` initialiser — the AsyncStorage path below handles it.
}

// Narrowed once here so every call site below gets a non-nullable
// reference without repeated `!` assertions — `SecureStore` itself stays
// nullable at module scope because the `require()` above may have failed.
const secureStore: SecureStoreModule | null =
  Platform.OS !== 'web' && SecureStore && typeof SecureStore.getItemAsync === 'function'
    ? SecureStore
    : null;
const useSecure = secureStore !== null;

/**
 * Adopt a value that lives in AsyncStorage into SecureStore. Covers two
 * cases: (1) a legacy plaintext token from a pre-upgrade install, and
 * (2) a token that `setToken` had to fall back to AsyncStorage for because
 * SecureStore rejected it (see the size-limit note there). The first time
 * `getToken` misses in SecureStore we look here, best-effort promote it into
 * SecureStore, and return it — so upgrades and oversized tokens both stay
 * seamless. If SecureStore rejects the write (oversized), we leave the value
 * in AsyncStorage rather than losing it.
 */
async function adoptFromAsyncStorage(store: SecureStoreModule, key: string): Promise<string | null> {
  const existing = await AsyncStorage.getItem(key);
  if (existing === null) return null;
  try {
    await store.setItemAsync(key, existing);
    await AsyncStorage.removeItem(key);
  } catch {
    // Couldn't move it into SecureStore (e.g. exceeds the ~2 KB value
    // limit) — keep it in AsyncStorage as the fallback and still return it.
  }
  return existing;
}

export async function getToken(key: string): Promise<string | null> {
  if (secureStore) {
    const value = await secureStore.getItemAsync(key);
    if (value !== null) return value;
    return adoptFromAsyncStorage(secureStore, key);
  }
  return AsyncStorage.getItem(key);
}

export async function setToken(key: string, value: string): Promise<void> {
  if (secureStore) {
    try {
      await secureStore.setItemAsync(key, value);
      // Best-effort cleanup: if a legacy/fallback plaintext copy is still in
      // AsyncStorage, drop it so we don't leave a stale token behind a fresh
      // SecureStore value.
      await AsyncStorage.removeItem(key).catch(() => undefined);
      return;
    } catch (err) {
      // SecureStore enforces a per-value size limit (~2 KB); a large token
      // carrying many claims can exceed it and throw. Fall back to
      // AsyncStorage so the session still
      // persists rather than failing login/refresh outright. Less protected
      // for that one value, but available beats broken. `console.warn`
      // survives the production console strip (warn/error are kept).
      console.warn('[token-storage] SecureStore write failed; using AsyncStorage fallback', err);
      await AsyncStorage.setItem(key, value);
      return;
    }
  }
  await AsyncStorage.setItem(key, value);
}

export async function removeToken(key: string): Promise<void> {
  if (secureStore) {
    await Promise.all([
      secureStore.deleteItemAsync(key),
      AsyncStorage.removeItem(key).catch(() => undefined),
    ]);
    return;
  }
  await AsyncStorage.removeItem(key);
}

export async function removeTokens(keys: string[]): Promise<void> {
  if (useSecure) {
    await Promise.all(keys.map((key) => removeToken(key)));
    return;
  }
  await AsyncStorage.multiRemove(keys);
}
